import React, { useState, useEffect } from "react";
import CustomTable from "../../theme/style-components/CustomTable";
import TabTitle from "../TabTitle";
import { Box, Typography, Button } from "@mui/material";
import AddEditEmployee from "./AddEditemployee";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  useDeleteEmployeeMutation,
  useGetEmployeeQuery,
  useLazyExportEmployeeExcelQuery,
  useLazyGetEmployeeExcelTemplateQuery,
  usePostEmployeeCsvMutation,
} from "../../store/employee/EmployeeApiSlice";
import moment from "moment";
import CustomButton from "../../theme/style-components/CustomButton";
import ImportCsv from "./ImportCsv";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUserObject } from "../../store/auth/authSlice";

const Employee = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchText, setSearchText] = useState("");
  const [brangerData, setBrangerData] = useState([]);
  const [employeeToDisplay, setEmployeeToDisplay] = useState([]);
  const [count, setCount] = useState(0);
  const [isEditEmployee, setIsEditEmployee] = useState(false);
  const [isImportCsv, setIsImportCsv] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const [getEmployeeExcelTemplate, { isFetching: isCsvLoading }] =
    useLazyGetEmployeeExcelTemplateQuery();
  const [exportEmployeeExcel, { isFetching: isExportLoading }] =
    useLazyExportEmployeeExcelQuery();
  const [deleteEmployee] = useDeleteEmployeeMutation();
  const user = useSelector(selectCurrentUserObject);

  const [query, setQuery] = useState(`org_id=${user?.company}`);
  const navigate = useNavigate();
  //base query hooks
  const {
    isLoading,
    data: employeeReturned,
    error: isEmployeeError,
    refetch: refetchEmployee,
  } = useGetEmployeeQuery({ page: page, query: query, search: searchText },{skip: !user?.company});

  useEffect(() => {
    if (!isLoading) {
      setEmployeeToDisplay(employeeReturned?.data);
      setCount(employeeReturned?.page_details?.count);
    }
  }, [isLoading, page, employeeReturned?.data, searchText]);

  const onPageChange = (newPage) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateClick = () => {
    navigate("/employee/add");
  };

  const handleUploadExcel = async (e) => {
    setIsImportCsv(true);
  };

  const handleExportExcel = () => {
    const org_id = user?.company;
    try {
      const templateData = exportEmployeeExcel(org_id)
        .unwrap()
        .then((res) => {
          const blob = new Blob([res], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "employee.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          err && toast.error("Something went wrong");
        });
    } catch (err) {
      err && toast.error("Something went wrong");
    }
  };

  const handleEditClick = () => {
    navigate(`/employee/edit/${selectedEmployee.id}`);
  };

  const handleDeleteClick = () => {
    try {
      const formData = new FormData();
      formData.append("employee_ids", selectedEmployee.id);
      deleteEmployee(formData)
        .unwrap()
        .then((res) => {
          toast.success("Employee deleted successfully");
          setIsMenuClicked(true);
          refetchEmployee();
        })
        .catch(() => {
          toast.error("Something went wrong!!");
        });
    } catch (error) {
      toast.error("Something went wrong!!");
    }
  };

  // Handle search input change
  const handleOnSearch = (event) => {
    setSearchText(event.target.value);
    setPage(1);
    setRowsPerPage(15);
    refetchEmployee();
  };

  const setSelectedData = (item) => {
    setSelectedEmployee(item);
  };

  const handleTemplateExcelDownload = async () => {
    try {
      const templateData = getEmployeeExcelTemplate()
        .unwrap()
        .then((res) => {
          const blob = new Blob([res], { type: "text/csv" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = "employee-template.csv";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        })
        .catch((err) => {
          err && toast.error("Something went wrong");
        });
    } catch (err) {
      err && toast.error("Something went wrong");
    }
  };

  const baseUrl = process.env.REACT_APP_MEDIA_URL

  const employeeData =
    employeeToDisplay?.length > 0
      ? employeeToDisplay.map((data) => ({
          id: data.id || "N/A",
          unique_emp_id: data.unique_emp_id || "N/A",
          name: data.name || "N/A",
          email: data.email || "N/A",
          phone: data.phone || "N/A",
          title: data.title || "N/A",
          office_location: data.office_location || "N/A",
          employment_type: data.employment_type || "N/A",
          date_of_joining: data.date_of_joining || "N/A",
          image_url: data.image  ? (
            <img src={`${baseUrl}${data?.image}`} alt="Profile" width={50} height={50} />
          ) :  (
            <img src={data?.image_url} alt="ProfileImg" width={50} height={50} />
          ) || "N/A",
          resume_url: data.resume_url ? (
            <a href={data.resume_url} target="_blank" rel="noopener noreferrer">
              View Resume
            </a>
          ) : (
            "N/A"
          ),
          linkedin_url: data.linkedin_url ? (
            <a
              href={data.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View LinkedIn
            </a>
          ) : (
            "N/A"
          ),
          job_description: data.job_description || "N/A",
          job_description_url: data.job_description_url ? (
            <a
              href={data.job_description_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Job Description
            </a>
          ) : (
            "N/A"
          ),
          created_at: data.created_at || "N/A",
          updated_at: data.updated_at || "N/A",
          organization: data.organization || "N/A",
          created_by: data.created_by || "N/A",
          managers:
            data.managers?.length > 0 ? data.managers.join(", ") : "N/A",
          departments:
            data.departments?.length > 0 ? data.departments.join(", ") : "N/A",
          teams: data.teams?.length > 0 ? data.teams.join(", ") : "N/A",
          certifications:
            data.certifications?.length > 0
              ? data.certifications.join(", ")
              : "N/A",
          action: "ACTION",
        }))
      : [];

  const columns = [
    { id: "id", label: "ID" },
    { id: "name", label: "Name" },
    { id: "email", label: "Email" },
    { id: "phone", label: "Phone" },
    { id: "title", label: "Title" },
    { id: "office_location", label: "Office Location" },
    { id: "employment_type", label: "Employment Type" },
    { id: "date_of_joining", label: "Date of Joining" },
    { id: "image_url", label: "Profile Image" },
    { id: "linkedin_url", label: "Linkedin URL" },
    { id: "action", label: "Action" },
  ];

  return (
    <>
      <Box>
        <TabTitle title="Employee" />

        <CustomTable
          title="Employee List"
          page={page}
          count={count}
          rowsPerPage={rowsPerPage}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          // Create button props
          handleCreateClick={handleCreateClick}
          createButtonName="Add Employee"
          // Edit menu props
          isShowEditMenu={true}
          handleEditClick={handleEditClick}
          isShowDeleteMenu={true}
          handleDelteClick={handleDeleteClick}
          isuploadexcel={true}
          handleUploadExcel={handleUploadExcel}
          isExporteExcel={true}
          handleExportExcel={handleExportExcel}
          isExcelTemplateDownload={true}
          handleTemplateExcelDownload={handleTemplateExcelDownload}
          isActionVisible={true}
          // Data props
          data={employeeData}
          columns={columns}
          setSelectedData={setSelectedData}
          // Search props
          isShowSearch={true}
          handleOnSearch={handleOnSearch}
          searchText={searchText}
          isMenuClicked={isMenuClicked}
          setIsMenuClicked={setIsMenuClicked}
          searchPlaceholder="Search Name"
        />
      </Box>

      <Box>
        {isEditEmployee && (
          <AddEditEmployee
            isEditEmployee={isEditEmployee}
            // setIsEditEmployee={setIsEditEmployee}
            selectedEmployee={selectedEmployee}
          />
        )}
      </Box>

      <Box>
        {isImportCsv && (
          <ImportCsv
            isImportCsv={isImportCsv}
            setIsImportCsv={setIsImportCsv}
          />
        )}
      </Box>
    </>
  );
};

export default Employee;
